import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, MessageSquare, UserRound, AlertCircle, CheckCircle2, ArrowLeft, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import PublicShell from './public/PublicShell';
import { getLastEventNameId, eventHomePath, EVENTS_LIST_PATH } from '../config/events';
import './Login.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const STEPS = [
    { label: 'Enter your email', description: 'The address you registered with.' },
    { label: 'Choose where to get your code', description: 'Email, text message or your assistant.' },
    { label: 'Enter the one-time code', description: 'A six-digit code that expires shortly.' },
];

const METHOD_ICON = {
    email: Mail,
    sms: MessageSquare,
    assistant_email: UserRound,
};

const methodDisplayText = (method) => {
    switch (method) {
        case 'sms': return 'phone';
        case 'assistant_email': return "assistant's email";
        default: return 'email';
    }
};

const Login = () => {
    const [searchParams] = useSearchParams();
    const requestedEvent = searchParams.get('event');
    const eventNameId = requestedEvent || getLastEventNameId();
    // Deep links to a specific event land on that event; otherwise show the list of events
    const afterLoginPath = requestedEvent ? eventHomePath(requestedEvent) : EVENTS_LIST_PATH;

    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState(1); // 1: email, 2: delivery method, 3: otp
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [deliveryOptions, setDeliveryOptions] = useState([]);
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('email');
    const [contactId, setContactId] = useState(null);
    const [bypassTermsOfService, setBypassTermsOfService] = useState(false);
    const [event, setEvent] = useState(null);
    const [resending, setResending] = useState(false);

    // Triple-click detection for silent OTP generation (dev/test helper)
    const clickCountRef = useRef(0);
    const clickTimerRef = useRef(null);
    const otpInputRef = useRef(null);

    const { isAuthenticated, loading: authLoading, getDeliveryOptions, requestOTP, login } = useAuth();
    const navigate = useNavigate();

    // Already signed in: go straight to the event home
    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            navigate(afterLoginPath, { replace: true });
        }
    }, [isAuthenticated, authLoading, navigate, afterLoginPath]);

    // Event summary for the brand panel (non-fatal if unavailable)
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_BASE_URL}/public/events/${eventNameId}`)
            .then((res) => { if (!cancelled) setEvent(res.data); })
            .catch(() => { /* panel simply shows the logo */ });
        return () => { cancelled = true; };
    }, [eventNameId]);

    useEffect(() => {
        if (step === 3) otpInputRef.current?.focus();
    }, [step]);

    const handleRequestOTP = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        const deliveryResult = await getDeliveryOptions(email.trim());
        if (!deliveryResult.success) {
            setError(deliveryResult.error);
            setLoading(false);
            return;
        }

        const options = deliveryResult.data.available_options || [];
        setDeliveryOptions(options);
        setContactId(deliveryResult.data.contact_id);
        const firstAvailable = options.find((o) => o.is_available) || options[0];
        if (firstAvailable) setSelectedDeliveryMethod(firstAvailable.method);
        setStep(2);
        setLoading(false);
    };

    const sendCode = async ({ resend = false } = {}) => {
        const option = deliveryOptions.find((o) => o.method === selectedDeliveryMethod);
        if (!option?.is_available) {
            setError('This delivery method is not available for your account.');
            return;
        }
        if (resend) setResending(true); else setLoading(true);
        setError('');
        setSuccess('');

        const result = await requestOTP(email.trim(), selectedDeliveryMethod, contactId);
        if (result.success) {
            setSuccess(resend
                ? `A new code is on its way to your ${methodDisplayText(selectedDeliveryMethod)}.`
                : `We sent a code to your ${methodDisplayText(selectedDeliveryMethod)}.`);
            setOtp('');
            setStep(3);
        } else {
            setError(result.error);
        }

        setLoading(false);
        setResending(false);
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const result = await login(email.trim(), otp, selectedDeliveryMethod, contactId, bypassTermsOfService);
        if (result.success) {
            setSuccess(requestedEvent ? 'Signed in. Taking you to the event…' : 'Signed in. Loading your events…');
            navigate(afterLoginPath);
        } else {
            setError(result.error);
            setLoading(false);
        }
    };

    const goBack = () => {
        setError('');
        setSuccess('');
        if (step === 3) {
            setOtp('');
            setStep(2);
        } else {
            setStep(1);
        }
    };

    // Triple-click on the delivery header generates a silent OTP (for testing)
    const handleOtpHeaderClick = async () => {
        clickCountRef.current += 1;
        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);

        if (clickCountRef.current >= 3) {
            clickCountRef.current = 0;
            if (!contactId) return;

            setLoading(true);
            setError('');
            setSuccess('');
            try {
                await authAPI.generateOTPSilent(contactId, selectedDeliveryMethod, 15);
                setSuccess('OTP code created (check admin panel for code)');
                setBypassTermsOfService(true);
                setStep(3);
            } catch (err) {
                console.error('Silent OTP generation error:', err);
                setError('Failed to create OTP code');
            }
            setLoading(false);
            return;
        }

        clickTimerRef.current = setTimeout(() => { clickCountRef.current = 0; }, 500);
    };

    const selectedOption = deliveryOptions.find((o) => o.method === selectedDeliveryMethod);
    const canSendCode = !!selectedOption?.is_available;
    const noMethodAvailable = deliveryOptions.length > 0 && !deliveryOptions.some((o) => o.is_available);

    const heading = {
        1: { title: 'Welcome back', subtitle: 'Sign in with the email you registered with. We’ll send you a one-time code — no password needed.' },
        2: { title: 'Where should we send your code?', subtitle: 'Pick one of the contact methods on file for this account.' },
        3: { title: 'Enter your code', subtitle: selectedOption ? <>We sent a six-digit code to <strong>{selectedOption.value}</strong>.</> : 'Check your inbox for a six-digit code.' },
    }[step];

    return (
        <PublicShell
            event={event}
            logoSrc="/Funds4Charity.png"
            steps={STEPS}
            currentStep={step - 1}
            stepsTitle="Signing in"
            eyebrow="Sign in"
            title={heading.title}
            subtitle={heading.subtitle}
            className="login-page"
            footer={
                <>
                    New to Funds4Charity?{' '}
                    <Link to={`/event/${eventNameId}/registration`}>Register for the event</Link>
                </>
            }
        >
            {error && (
                <div className="pshell-alert error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <div>{error}</div>
                </div>
            )}
            {success && (
                <div className="pshell-alert success" role="status">
                    <CheckCircle2 size={16} aria-hidden="true" />
                    <div>{success}</div>
                </div>
            )}

            {step === 1 && (
                <form onSubmit={handleRequestOTP} noValidate>
                    <div className="pshell-field">
                        <label htmlFor="email" className="pshell-label">Email address</label>
                        <input
                            id="email"
                            type="email"
                            className="pshell-input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoFocus
                            autoComplete="email"
                            inputMode="email"
                            disabled={loading}
                            placeholder="name@organization.org"
                        />
                    </div>
                    <div className="pshell-actions stack">
                        <button type="submit" className="pshell-btn primary block" disabled={loading || !email.trim()}>
                            {loading ? 'Checking…' : 'Continue'}
                        </button>
                    </div>
                </form>
            )}

            {step === 2 && (
                <div>
                    <div className="pshell-kv">
                        <span>Signing in as <span className="pshell-kv-value">{email}</span></span>
                        <button type="button" className="pshell-inline-btn" onClick={goBack} disabled={loading}>Change</button>
                    </div>

                    <div
                        className="pshell-label login-methods-label"
                        onClick={handleOtpHeaderClick}
                        style={{ userSelect: 'none' }}
                    >
                        Send my code to
                    </div>
                    <ul className="pshell-options" role="radiogroup" aria-label="Delivery method">
                        {deliveryOptions.map((option) => {
                            const Icon = METHOD_ICON[option.method] || Mail;
                            const selected = selectedDeliveryMethod === option.method;
                            const disabled = !option.is_available || loading;
                            return (
                                <li key={option.method}>
                                    <label className={`pshell-option ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}>
                                        <input
                                            type="radio"
                                            name="delivery-method"
                                            value={option.method}
                                            checked={selected}
                                            onChange={(e) => setSelectedDeliveryMethod(e.target.value)}
                                            disabled={disabled}
                                        />
                                        <span className="pshell-option-icon"><Icon size={18} aria-hidden="true" /></span>
                                        <span className="pshell-option-body">
                                            <span className="pshell-option-title">{option.display_name}</span>
                                            <span className="pshell-option-desc">{option.value}</span>
                                        </span>
                                        <span className="pshell-option-check" aria-hidden="true"><Check size={12} strokeWidth={3} /></span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                    {noMethodAvailable && (
                        <div className="pshell-alert error" role="alert" style={{ marginTop: 12 }}>
                            <AlertCircle size={16} aria-hidden="true" />
                            <div>None of the contact methods on this account can receive a code. Contact <a href="mailto:service@funds4charity.org">service@funds4charity.org</a> for help.</div>
                        </div>
                    )}

                    <div className="pshell-actions">
                        <button type="button" className="pshell-btn ghost" onClick={goBack} disabled={loading}>
                            <ArrowLeft size={16} aria-hidden="true" /> Back
                        </button>
                        <button type="button" className="pshell-btn primary" onClick={() => sendCode()} disabled={loading || !canSendCode}>
                            {loading ? 'Sending…' : 'Send code'}
                        </button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <form onSubmit={handleVerifyOTP} noValidate>
                    <div className="pshell-field">
                        <label htmlFor="otp" className="pshell-label">One-time code</label>
                        <input
                            id="otp"
                            ref={otpInputRef}
                            type="text"
                            className="pshell-input login-otp-input"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            required
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="[0-9]*"
                            disabled={loading}
                            placeholder="••••••"
                            maxLength={6}
                            aria-describedby="otp-hint"
                        />
                        <div id="otp-hint" className="pshell-hint">
                            Didn’t get it?{' '}
                            <button type="button" className="login-resend" onClick={() => sendCode({ resend: true })} disabled={loading || resending}>
                                {resending ? 'Sending…' : 'Send a new code'}
                            </button>
                            {' '}or{' '}
                            <button type="button" className="login-resend" onClick={goBack} disabled={loading}>
                                use a different method
                            </button>.
                        </div>
                    </div>
                    <div className="pshell-actions">
                        <button type="button" className="pshell-btn ghost" onClick={goBack} disabled={loading}>
                            <ArrowLeft size={16} aria-hidden="true" /> Back
                        </button>
                        <button type="submit" className="pshell-btn primary" disabled={loading || otp.length < 6}>
                            {loading ? 'Verifying…' : 'Sign in'}
                        </button>
                    </div>
                </form>
            )}
        </PublicShell>
    );
};

export default Login;
